/**
 * Audit Log Types and Constants
 * Types for audit log entries, query params, and display labels
 */

// ============================================================================
// ENUMS & CONSTANTS
// ============================================================================

export type AuditAction = 'CREATE' | 'UPDATE' | 'DELETE' | 'VERSION'

export type AuditEntityType =
  | 'AttributeDefinition'
  | 'AttributeHierarchy'
  | 'PolicyDraft'
  | 'Conversation'
  | 'Template'
  | 'SystemConfig'
  | 'DatabaseTable'

export type DecisionOutcome = 'PERMIT' | 'DENY'

export type ExportFormat = 'csv' | 'json'

export const AUDIT_ACTIONS: AuditAction[] = ['CREATE', 'UPDATE', 'DELETE', 'VERSION']

export const AUDIT_ENTITY_TYPES: AuditEntityType[] = [
  'AttributeDefinition',
  'AttributeHierarchy',
  'PolicyDraft',
  'Conversation',
  'Template',
  'SystemConfig',
  'DatabaseTable',
]

export const DECISION_OUTCOMES: DecisionOutcome[] = ['PERMIT', 'DENY']

// ============================================================================
// DISPLAY LABELS
// ============================================================================

export const ACTION_LABELS: Record<AuditAction, string> = {
  CREATE: 'Create',
  UPDATE: 'Update',
  DELETE: 'Delete',
  VERSION: 'Version',
}

export const ENTITY_TYPE_LABELS: Record<AuditEntityType, string> = {
  AttributeDefinition: 'Attribute Definition',
  AttributeHierarchy: 'Attribute Hierarchy',
  PolicyDraft: 'Policy Draft',
  Conversation: 'Conversation',
  Template: 'Template',
  SystemConfig: 'System Config',
  DatabaseTable: 'Database Table',
}

export const OUTCOME_LABELS: Record<DecisionOutcome, string> = {
  PERMIT: 'Permit',
  DENY: 'Deny',
}

// ============================================================================
// API TYPES
// ============================================================================

export interface AuditMetadata {
  requestId?: string
  ipAddress?: string
  userAgent?: string
  reason?: string
  correlationId?: string
}

export interface AuditLogEntry {
  id: string
  tenantId: string
  actorId: string
  actorName?: string
  actorEmail?: string
  action: AuditAction
  entityType: string
  entityId: string
  oldValue: unknown
  newValue: unknown
  metadata?: AuditMetadata | null
  createdAt: string
  outcome?: DecisionOutcome
}

export interface AuditLogQueryParams {
  page?: number
  pageSize?: number
  startDate?: string
  endDate?: string
  actorId?: string
  actorSearch?: string
  resourceFilter?: string
  entityType?: string
  action?: AuditAction
  outcome?: DecisionOutcome
  sortBy?: 'createdAt'
  sortOrder?: 'asc' | 'desc'
}

export interface AuditLogExportParams {
  startDate?: string
  endDate?: string
  actorId?: string
  actorSearch?: string
  resourceFilter?: string
  entityType?: string
  action?: AuditAction
  outcome?: DecisionOutcome
  format: ExportFormat
}

export interface Pagination {
  page: number
  pageSize: number
  totalItems: number
  totalPages: number
  hasNextPage: boolean
  hasPreviousPage: boolean
}

export interface AuditLogListResponse {
  data: AuditLogEntry[]
  pagination: Pagination
}
